export async function fetchJsonWithBusyRetry(url, {
    signal,
    fetchImpl = fetch,
    maxRetries = 2,
    maxRetryDelayMs = 2000,
    timeoutMs = 10000,
    onResponse = null
} = {}) {
    // Sin este limite, una peticion que nunca resuelve (conexion colgada,
    // cuerpo que no termina de llegar) deja `isLoading` en true para siempre
    // en cualquier pantalla que use esta funcion -- ni el catch ni el
    // reintento automatico llegan a dispararse porque la promesa nunca se
    // rechaza. Vive aqui (no en cada llamador) para que ningun sitio nuevo
    // pueda olvidarlo, que es justo como se colo la primera vez.
    const internalController = new AbortController();
    const onExternalAbort = () => internalController.abort();
    if (signal) {
        if (signal.aborted) internalController.abort();
        else signal.addEventListener('abort', onExternalAbort, { once: true });
    }

    const attempt = async () => {
        for (let attemptIndex = 0; attemptIndex <= maxRetries; attemptIndex += 1) {
            const response = await fetchImpl(url, { signal: internalController.signal });
            if (response.ok) {
                if (onResponse) onResponse(response);
                return response.json();
            }

            const canRetry = (response.status === 429 || response.status === 503) && attemptIndex < maxRetries;
            if (!canRetry) {
                const error = new Error(`HTTP ${response.status}`);
                error.status = response.status;
                throw error;
            }

            const retryAfterMs = parseRetryAfterMs(response.headers?.get?.('Retry-After'));
            await waitForRetry(Math.min(maxRetryDelayMs, retryAfterMs), internalController.signal);
        }
        throw new Error('Retry budget exhausted');
    };

    let timeoutId;
    // Distinto de AbortError a proposito: un abort real de cancelacion
    // (desmontaje, filtro nuevo) debe seguir ignorandose donde ya se
    // ignoraba, pero un timeout es un fallo real que debe activar los
    // mismos reintentos/mensajes de error que un 503 agotado.
    const timeoutPromise = new Promise((_resolve, reject) => {
        timeoutId = setTimeout(() => {
            internalController.abort();
            reject(createTimeoutError());
        }, timeoutMs);
    });

    try {
        return await Promise.race([attempt(), timeoutPromise]);
    } finally {
        clearTimeout(timeoutId);
        if (signal) signal.removeEventListener('abort', onExternalAbort);
    }
}

function parseRetryAfterMs(value) {
    const seconds = Number(value);
    if (Number.isFinite(seconds) && seconds >= 0) return Math.max(100, seconds * 1000);
    const dateMs = Date.parse(String(value || ''));
    if (Number.isFinite(dateMs)) return Math.max(100, dateMs - Date.now());
    return 500;
}

function waitForRetry(delayMs, signal) {
    if (signal?.aborted) return Promise.reject(createAbortError());
    return new Promise((resolve, reject) => {
        const timer = setTimeout(() => {
            signal?.removeEventListener('abort', onAbort);
            resolve();
        }, delayMs);
        const onAbort = () => {
            clearTimeout(timer);
            signal?.removeEventListener('abort', onAbort);
            reject(createAbortError());
        };
        signal?.addEventListener('abort', onAbort, { once: true });
    });
}

function createAbortError() {
    const error = new Error('The operation was aborted');
    error.name = 'AbortError';
    return error;
}

function createTimeoutError() {
    const error = new Error('Request timed out');
    error.name = 'TimeoutError';
    return error;
}
